/**
 * 구역 관리를 위한 커스텀 훅
 */
import { useState, useEffect, useCallback } from 'react';
import {
  zoneApi,
  Zone,
  ZoneWithSeats,
  StudyRoomLayout,
  ZoneSummary,
} from '../services/zoneApi';

export const useZones = () => {
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 모든 구역 목록 불러오기
   */
  const loadZones = useCallback(
    async (params?: { zone_type?: string; is_active?: boolean }) => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await zoneApi.getZones(params);
        setZones(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : '구역을 불러오는데 실패했습니다'
        );
        console.error('Failed to load zones:', err);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * 초기 데이터 로드
   */
  useEffect(() => {
    loadZones({ is_active: true });
  }, [loadZones]);

  /**
   * 구역 선택
   */
  const selectZone = useCallback((zone: Zone | null) => {
    setSelectedZone(zone);
  }, []);

  /**
   * 구역 새로고침
   */
  const refreshZones = useCallback(() => {
    loadZones({ is_active: true });
  }, [loadZones]);

  return {
    zones,
    selectedZone,
    isLoading,
    error,
    selectZone,
    loadZones,
    refreshZones,
  };
};

export const useZoneWithSeats = (zoneId: number | null) => {
  const [zoneWithSeats, setZoneWithSeats] = useState<ZoneWithSeats | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 구역과 좌석 정보 불러오기
   */
  const loadZoneWithSeats = useCallback(async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await zoneApi.getZoneWithSeats(id);
      setZoneWithSeats(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : '구역 정보를 불러오는데 실패했습니다'
      );
      console.error('Failed to load zone with seats:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (zoneId) {
      loadZoneWithSeats(zoneId);
    } else {
      setZoneWithSeats(null);
    }
  }, [zoneId, loadZoneWithSeats]);

  /**
   * 구역 정보 새로고침
   */
  const refreshZoneWithSeats = useCallback(() => {
    if (zoneId) {
      loadZoneWithSeats(zoneId);
    }
  }, [zoneId, loadZoneWithSeats]);

  return {
    zoneWithSeats,
    isLoading,
    error,
    refreshZoneWithSeats,
  };
};

export const useFloorLayout = (floorLevel: number) => {
  const [floorLayout, setFloorLayout] = useState<StudyRoomLayout | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 층별 레이아웃 정보 불러오기
   */
  const loadFloorLayout = useCallback(async (floor: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await zoneApi.getFloorLayout(floor);
      setFloorLayout(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : '층 레이아웃을 불러오는데 실패했습니다'
      );
      console.error('Failed to load floor layout:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFloorLayout(floorLevel);
  }, [floorLevel, loadFloorLayout]);

  /**
   * 레이아웃 새로고침
   */
  const refreshFloorLayout = useCallback(() => {
    loadFloorLayout(floorLevel);
  }, [floorLevel, loadFloorLayout]);

  return {
    floorLayout,
    isLoading,
    error,
    refreshFloorLayout,
  };
};

export const useZonesSummary = () => {
  const [summary, setSummary] = useState<ZoneSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 구역별 요약 정보 불러오기
   */
  const loadSummary = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await zoneApi.getZonesSummary();
      setSummary(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : '구역 요약 정보를 불러오는데 실패했습니다'
      );
      console.error('Failed to load zones summary:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  /**
   * 요약 정보 새로고침
   */
  const refreshSummary = useCallback(() => {
    loadSummary();
  }, [loadSummary]);

  return {
    summary,
    isLoading,
    error,
    refreshSummary,
  };
};
